// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  Chip,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import type { SvgIconName } from '@shared/ui-kit/components/app-icon';
import {
  AppIcon,
  classNames,
  defineComponent,
  formatToPercent,
  Table,
} from '@shared/ui-kit';
import { Translation } from '@shared/ui-kit/providers/translation-provider';
import { IlliquidAssetType } from '@shared/data-access/types/engine-types';
import { ObjectRowTags } from '@shared/advisory/aggregation/components/tags';
import { TagObjectType } from '@shared/data-access/interfaces/redux/tags-management-state-types';
import styles from './iliquid-assets.module.css';
import { useIlliquidAssetsList } from './use-illiquid-assets-list';

const LIST_ICON_BY_ASSET_TYPE: Record<IlliquidAssetType, SvgIconName> = {
  [IlliquidAssetType.RealEstate]: 'Building',
  [IlliquidAssetType.Company]: 'Briefcase',
  [IlliquidAssetType.PeVc]: 'TrendingUp',
  [IlliquidAssetType.Tangible]: 'Palette',
  [IlliquidAssetType.OtherIlliquid]: 'Boxes',
};

export const IlliquidAssetsList = defineComponent({
  displayName: 'IlliquidAssetsList',
  useSetup: useIlliquidAssetsList,
  View: ({ assets, onAssetClick, currency }) => (
    <List disablePadding className={styles.list}>
      {assets.map((asset, index) => (
        <ListItem
          key={asset.id}
          disablePadding
          className={styles['row-item']}
          secondaryAction={
            <div className={styles['secondary-action-wrap']}>
              <AppIcon icon="ArrowRight" color="inherit" size="lg" />
            </div>
          }
        >
          <ListItemButton
            component="div"
            onClick={() => onAssetClick?.(asset)}
            className={classNames(
              styles['row-button'],
              index === assets.length - 1 && styles['row-button-last'],
            )}
          >
            <ListItemIcon>
              <div className={styles['icon-wrapper']}>
                <AppIcon
                  icon={LIST_ICON_BY_ASSET_TYPE[asset.category] ?? 'Building'}
                  color="inherit"
                  size="lg"
                />
              </div>
            </ListItemIcon>

            <ListItemText
              primaryTypographyProps={{
                variant: 'h6',
                className: styles['primary-name'],
              }}
              primary={asset.name}
              secondary={
                <>
                  <span className={styles['subcategory-part']}>
                    <Translation
                      i18nKey={
                        `illiquid-asset-drawer.options.subcategory.${asset.category}.${asset.subcategory}` as any
                      }
                      ns="components"
                    />
                  </span>
                  {asset.ownership_pct !== null ? (
                    <Chip
                      size="small"
                      variant="outlined"
                      color="primary"
                      className={classNames(
                        styles['ownership-chip'],
                        '!money-display',
                      )}
                      label={
                        <Translation
                          i18nKey="aggregator.illiquid-asset-ownership-line"
                          ns="aggregator"
                          values={{
                            percent: formatToPercent(asset.ownership_pct),
                          }}
                        />
                      }
                    />
                  ) : null}
                </>
              }
              secondaryTypographyProps={{
                variant: 'body2',
                component: 'div',
                className: styles['secondary-line'],
              }}
            />

            <div className={styles['tags-slot']}>
              <ObjectRowTags
                objectType={TagObjectType.IlliquidAsset}
                objectId={String(asset.id)}
              />
            </div>

            <ListItemText
              className={styles['balance-cell']}
              primaryTypographyProps={{
                className: classNames(styles['balance-value'], '!money-display'),
              }}
              primary={
                <Table.Data
                  value={asset.current_valuation}
                  format="currency"
                  currency={currency}
                  safeValue="-"
                  emptyValue={null}
                />
              }
            />
          </ListItemButton>
        </ListItem>
      ))}
    </List>
  ),
});
